import { type RefObject, useEffect } from 'react';

const OUTER_GAP = 16;
const INNER_GAP = 2;
const OVERLAY_SELECTOR = '[data-viewport-bottom-overlay]';

const getCaretRect = (textArea: HTMLTextAreaElement, window: Window) => {
    const document = textArea.ownerDocument;
    const style = window.getComputedStyle(textArea);
    const mirror = document.createElement('div');
    const marker = document.createElement('span');
    const horizontalPadding =
        (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0);

    Object.assign(mirror.style, {
        position: 'fixed',
        inset: '0 auto auto 0',
        visibility: 'hidden',
        pointerEvents: 'none',
        overflow: 'hidden',
        boxSizing: 'content-box',
        width: `${Math.max(0, textArea.clientWidth - horizontalPadding)}px`,
        direction: style.direction,
        padding: style.padding,
        font: style.font,
        textAlign: style.textAlign,
        textTransform: style.textTransform,
        textIndent: style.textIndent,
        letterSpacing: style.letterSpacing,
        wordSpacing: style.wordSpacing,
        tabSize: style.tabSize,
        whiteSpace: style.whiteSpace,
        wordBreak: style.wordBreak,
        overflowWrap: style.overflowWrap,
    });
    mirror.setAttribute('aria-hidden', 'true');
    const suffix = textArea.value.substring(textArea.selectionEnd);

    mirror.textContent = textArea.value.substring(0, textArea.selectionEnd);
    mirror.append(marker, suffix || '\uFEFF');
    document.body.appendChild(mirror);

    try {
        const textAreaRect = textArea.getBoundingClientRect();
        const markerRect = marker.getBoundingClientRect();
        const mirrorRect = mirror.getBoundingClientRect();
        const lineHeight =
            Number.parseFloat(style.lineHeight) || Number.parseFloat(style.fontSize) * 1.2;
        const top =
            textAreaRect.top +
            textArea.clientTop +
            markerRect.top -
            mirrorRect.top -
            textArea.scrollTop;

        return { top, bottom: top + lineHeight };
    } finally {
        mirror.remove();
    }
};

export const useKeepTextAreaVisible = (textAreaRef: RefObject<HTMLTextAreaElement | null>) => {
    useEffect(() => {
        const textArea = textAreaRef.current;

        if (!textArea) {
            return;
        }

        const document = textArea.ownerDocument;
        const window = document.defaultView;
        const viewport = window?.visualViewport;

        if (!window || !viewport) {
            return;
        }

        const correctScroll = () => {
            if (
                document.activeElement !== textArea ||
                textArea.selectionStart !== textArea.selectionEnd
            ) {
                return;
            }

            const viewportBottom = viewport.offsetTop + viewport.height;
            const overlayRect = document
                .querySelector<HTMLElement>(OVERLAY_SELECTOR)
                ?.getBoundingClientRect();

            if (
                !overlayRect ||
                overlayRect.top >= viewportBottom ||
                overlayRect.bottom < viewportBottom - 1
            ) {
                return;
            }

            const caretRect = getCaretRect(textArea, window);

            const textAreaRect = textArea.getBoundingClientRect();
            const innerTop = textAreaRect.top + textArea.clientTop;
            const innerBottom = innerTop + textArea.clientHeight;
            let innerDelta = 0;

            if (caretRect.bottom > innerBottom - INNER_GAP) {
                innerDelta = caretRect.bottom - innerBottom + INNER_GAP;
            } else if (caretRect.top < innerTop + INNER_GAP) {
                innerDelta = caretRect.top - innerTop - INNER_GAP;
            }

            if (Math.abs(innerDelta) > 1) {
                const previousScrollTop = textArea.scrollTop;

                textArea.scrollTop += Math.round(innerDelta);
                const appliedDelta = textArea.scrollTop - previousScrollTop;
                caretRect.top -= appliedDelta;
                caretRect.bottom -= appliedDelta;
            }

            const outerDelta = caretRect.bottom - (overlayRect.top - OUTER_GAP);

            if (outerDelta > 1) {
                window.scrollBy(0, Math.round(outerDelta));
            }
        };

        let correctionFrame: number | undefined;
        const scheduleCorrection = () => {
            if (correctionFrame !== undefined) {
                return;
            }

            correctionFrame = window.requestAnimationFrame(() => {
                correctionFrame = undefined;
                correctScroll();
            });
        };
        const controller = new AbortController();
        const listen = (target: EventTarget, events: string[], listener: EventListener) =>
            events.forEach((event) =>
                target.addEventListener(event, listener, {
                    capture: true,
                    signal: controller.signal,
                }),
            );

        listen(window, ['resize'], correctScroll);
        listen(viewport, ['resize'], correctScroll);
        listen(window, ['scroll'], scheduleCorrection);
        listen(viewport, ['scroll'], scheduleCorrection);
        listen(textArea, ['input', 'scroll', 'focus', 'click', 'select'], scheduleCorrection);
        scheduleCorrection();

        return () => {
            controller.abort();
            window.cancelAnimationFrame(correctionFrame ?? 0);
        };
    }, [textAreaRef]);
};
